<script lang="ts">
    import { createEventDispatcher } from "svelte"
    import { _ } from "svelte-i18n"
    import Icon from "../../../../widgets/Icon.svelte"
    import { mdiInformationOutline, mdiMagnify } from "@mdi/js"
    import Modal from "../../../../widgets/Modal.svelte"

    import { RuleEngine } from "../RuleEngine"
    import InfoText from "./InfoText.svelte"

    export let ftpAssetsUrl: string

    // Choices from MailboxFilter
    export let currentChoices = null

    export let name = null
    export let value
    export let values: { [key: string]: any }
    export let multiple: boolean = false
    export let dependsOn: OfferAssistantDependsOnRule[]
    export let label: string
    export let css: string
    export let filterPlaceholder: string
    export let showFilterIfMoreThan: number
    export let highlightedValue: string | number
    export let choices: {
        id: number | string
        title: string
        description: string
        informationText: string
        imageUrl: string
        color: string
    }[]
    values = values || null

    let activeModal: {
        [key: number]: boolean
    } = {}
    const ruleEngine = new RuleEngine()
    let displayComponent: boolean = true
    let showChoiceNotSelectableInfo: boolean = false
    const dispatch = createEventDispatcher()

    let filterInputValue = ""
    $: filteredChoices = choices.filter((choice) => {
        if (filterInputValue === "") {
            return true
        }
        if (choice.title && choice.title.toLowerCase().includes(filterInputValue.toLowerCase())) {
            return true
        }
        if (choice.description && choice.description.toLowerCase().includes(filterInputValue.toLowerCase())) {
            return true
        }

        return false
    })

    // Set initial default value for multiple select
    $: if (multiple && !value) {
        value = []
    }

    // Takeover selected mailbox total value from MailboxFilter(Konfigurator) and overwrite any default values
    if (!value || (Array.isArray(value) && !value.length)) {
        if (!value) {
            if (currentChoices && currentChoices[name] && value !== currentChoices[name]) {
                value = currentChoices[name]
            }
        } else if (Array.isArray(value)) {
            value.push(currentChoices[name])
        }
    }

    const select = (choice) => {
        if (multiple) {
            if (!Array.isArray(value)) {
                value = []
            }
            if (value?.includes(choice.id)) {
                let index = value.indexOf(choice.id)
                value.splice(index, 1)
            } else {
                value.push(choice.id)
            }
        } else {
            if (!value) {
                value = choice.id
            } else if (value && value === choice.id) {
                value = null
            } else {
                value = choice.id
            }
        }

        value = value
    }

    const isChoiceResolvedDependsOn = (choice, values) => {
        if (choice.dependsOn && values) {
            let resolved = ruleEngine.resolve(choice.dependsOn, values)

            if (!resolved && choice.id === value) {
                value = null // Selection have to reset when choice not selectable
                updateChoiceNotSelectableInfo(true)
            } else if (resolved && choice.id === value) {
                updateChoiceNotSelectableInfo(false)
            }

            return resolved
        } else if (!choice.dependsOn && values) {
            if (choice.id === value) {
                updateChoiceNotSelectableInfo(false)
            }
        }

        return true
    }

    const updateChoiceNotSelectableInfo = (show: boolean) => {
        showChoiceNotSelectableInfo = show
        dispatch("invalid-config", showChoiceNotSelectableInfo)
    }

    const isExistsSelectedOption = () => {
        for (let i = 0; i < choices.length; i++) {
            if (choices[i].id === value) {
                // Invalid config check
                if (displayComponent) {
                    // Trigger event to show invalid config info in step AND in component
                    updateChoiceNotSelectableInfo(true)
                } else {
                    // Trigger event to show invalid config info in step but NOT in component
                    dispatch("invalid-config", true)
                }

                return true
            }
        }

        return false
    }

    const nl2br = (str) => {
        if (typeof str === "undefined" || str === null) {
            return ""
        }
        var breakTag = "<br/>"
        return (str + "").replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1" + breakTag + "$2")
    }

    $: if (dependsOn) {
        displayComponent = ruleEngine.resolve(dependsOn, values)

        // Invalid config check
        isExistsSelectedOption()

        if (!displayComponent) {
            value = null
        }
    }
</script>

{#if displayComponent}
    <div class="image-select">
        {#if label}
            <label for="">{label}</label>
        {/if}

        {#if showChoiceNotSelectableInfo}
            <InfoText
                css="warning"
                label="Achtung!"
                text="Die zuvor gewählte Option ist nicht mehr passend zur aktuellen Konfiguration. Bitte wählen Sie erneut eine der verfügbaren Optionen."
                values="{{}}"
            />
        {/if}

        {#if choices.length > (showFilterIfMoreThan ? showFilterIfMoreThan : 10)}
            <form class="image-select-filter">
                <Icon path="{mdiMagnify}" />
                <input
                    type="text"
                    bind:value="{filterInputValue}"
                    placeholder="{filterPlaceholder ? filterPlaceholder : $_('product.search-color')}"
                />
            </form>
        {/if}

        <div class="image-select-choices">
            {#each filteredChoices || [] as choice, i}
                {#if isChoiceResolvedDependsOn(choice, values)}
                    <div
                        class="image-select-item"
                        style="{css}"
                        class:standard-variant="{highlightedValue === choice.id || choice.highlighted}"
                    >
                        {#if choice.color}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <div
                                class="image-select-color"
                                style="{`background: ${choice.color}`}"
                                class:selected="{value && multiple ? value?.includes(choice.id) : value === choice.id}"
                                on:click="{() => select(choice)}"
                            ></div>
                        {:else if choice.imageUrl}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <img
                                src="{ftpAssetsUrl}{choice.imageUrl}"
                                alt="{choice.title}"
                                class:selected="{value && multiple ? value?.includes(choice.id) : value === choice.id}"
                                on:click="{() => select(choice)}"
                            />
                        {/if}
                        {#if choice.title}
                            <div class="image-select-title">{choice.title}</div>
                        {/if}
                        {#if choice.description}
                            <div class="image-select-description">
                                {choice.description}
                            </div>
                        {/if}
                        {#if choice.informationText}
                            <!-- svelte-ignore a11y-click-events-have-key-events -->
                            <div
                                class="image-select-information-icon"
                                on:click|stopPropagation="{() => {
                                    activeModal[i] = true
                                }}"
                            >
                                <Icon path="{mdiInformationOutline}" />
                            </div>
                            <Modal bind:show="{activeModal[i]}">
                                {@html nl2br(choice.informationText)}
                            </Modal>
                        {/if}
                    </div>
                {/if}
            {/each}
        </div>
    </div>
{/if}
